import React, { useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Empty, Flex, Input, Modal, Segmented, Spin, Table, Tag, Tooltip, Typography, theme } from 'antd';
import { AimOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ApiError, searchNiches, type Niche } from '../../shared/api/client';
import HintLabel from '../../shared/ui/HintLabel';
import { bindSnapshot, formatCount, formatPrice, formatShare, nicheErrorKey, nicheFill, type NicheFill } from './nicheBindingLogic';
import { isAsin } from '../../shared/lib/asin';

interface NicheBindingProps {
  /** ASIN из калькулятора — по нему ищем в первую очередь. */
  asin: string;
  niche: Niche | null;
  onBind: (niche: Niche, fill: NicheFill) => void;
  onUnbind: () => void;
}

type Mode = 'asin' | 'keyword';
type View = { kind: 'closed' } | { kind: 'search' } | { kind: 'detail'; niche: Niche; preview: boolean };

/**
 * Привязка ниши Opportunity Explorer к расчёту (WS 15587410, п.2; фидбек 29.09).
 *
 * «Найти нишу» ищет по ASIN сразу; ниши нет — поиск по ключу. У каждой найденной
 * ниши две кнопки: «Открыть» (детали до привязки) и «Привязать». Привязка
 * подставляет конверсию (search conversion × 2) и return rate ниши и сохраняет
 * снимок ниши в расчёте. Название привязанной ниши видно в калькуляторе, по клику —
 * детали.
 */
const NicheBinding: React.FC<NicheBindingProps> = ({ asin, niche, onBind, onUnbind }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const asinOk = isAsin(asin);
  const [view, setView] = useState<View>({ kind: 'closed' });
  const [mode, setMode] = useState<Mode>(asinOk ? 'asin' : 'keyword');
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<Niche[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (query: { asin: string } | { keyword: string }) => {
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      setResults(await searchNiches(query));
    } catch (e: unknown) {
      setError(t(nicheErrorKey(e instanceof ApiError ? e.status : undefined)));
    } finally {
      setLoading(false);
    }
  };

  // По ASIN ищем сразу при открытии: это основной путь из задачи.
  useEffect(() => {
    if (view.kind === 'search' && mode === 'asin' && asinOk && results === null && !loading && !error) {
      void run({ asin });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.kind, mode]);

  const openSearch = () => {
    setMode(asinOk ? 'asin' : 'keyword');
    setResults(null);
    setError(null);
    setView({ kind: 'search' });
  };

  const bind = (n: Niche) => {
    onBind(bindSnapshot(n), nicheFill(n));
    setView({ kind: 'closed' });
  };

  const stats = (n: Niche) => [
    { key: 'sv', label: t('idea_calc.niche.stat_search_volume'), value: formatCount(n.search_volume_t360, i18n.language) },
    { key: 'growth', label: t('idea_calc.niche.stat_growth'), value: formatShare(n.search_volume_growth_t180) },
    { key: 'conv', label: t('idea_calc.niche.stat_conversion'), value: formatShare(n.search_conversion_rate) },
    { key: 'rr', label: t('idea_calc.niche.stat_return_rate'), value: formatShare(n.return_rate) },
    { key: 'products', label: t('idea_calc.niche.stat_products'), value: formatCount(n.product_count, i18n.language) },
    { key: 'price', label: t('idea_calc.niche.stat_avg_price'), value: formatPrice(n.avg_price) },
    { key: 'top5', label: t('idea_calc.niche.stat_top5'), value: formatShare(n.top5_click_share) },
  ];

  return (
    <div data-testid="niche-binding">
      {niche ? (
        <Flex align="center" gap="small" wrap>
          <Typography.Text type="secondary">{t('idea_calc.niche.label')}</Typography.Text>
          <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setView({ kind: 'detail', niche, preview: false })}>
            {niche.title}
          </Button>
          <Tag><HintLabel hint={t('idea_calc.niche.tips.fill')}>{t('idea_calc.niche.fill_hint')}</HintLabel></Tag>
        </Flex>
      ) : (
        <Button size="small" icon={<AimOutlined />} onClick={openSearch}>
          {t('idea_calc.niche.find_button')}
        </Button>
      )}

      <Modal
        open={view.kind === 'search'}
        onCancel={() => setView({ kind: 'closed' })}
        footer={null}
        width={900}
        title={t('idea_calc.niche.search_title')}
        destroyOnClose
      >
        <Flex vertical gap="middle">
          <Segmented<Mode>
            value={mode}
            onChange={(m) => {
              setMode(m);
              setResults(null);
              setError(null);
            }}
            options={[
              { value: 'asin', label: t('idea_calc.niche.by_asin', { asin: asin || '—' }), disabled: !asinOk },
              { value: 'keyword', label: t('idea_calc.niche.by_keyword') },
            ]}
          />
          {mode === 'keyword' && (
            <Input.Search
              placeholder={t('idea_calc.niche.keyword_placeholder')}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onSearch={(v) => {
                if (v.trim().length >= 2) void run({ keyword: v.trim() });
              }}
              enterButton={<SearchOutlined />}
              loading={loading}
            />
          )}
          {loading && (
            <Flex justify="center" gap="small" style={{ padding: token.padding }}>
              <Spin />
              <Typography.Text type="secondary">{t('idea_calc.niche.loading')}</Typography.Text>
            </Flex>
          )}
          {error && <Alert type="error" showIcon message={error} />}
          {results && results.length === 0 && (
            <Empty
              description={mode === 'asin' ? t('idea_calc.niche.empty_asin') : t('idea_calc.niche.empty_keyword')}
            >
              {mode === 'asin' && (
                <Button type="link" onClick={() => setMode('keyword')}>
                  {t('idea_calc.niche.try_keyword')}
                </Button>
              )}
            </Empty>
          )}
          {results && results.length > 0 && (
            <>
              <Typography.Text type="secondary">
                {mode === 'asin' ? t('idea_calc.niche.found_by_asin', { count: results.length }) : t('idea_calc.niche.found_by_keyword', { count: results.length })}
              </Typography.Text>
              <Flex vertical gap="small">
                {results.map((n) => (
                  <Flex
                    key={n.niche_id}
                    align="center"
                    gap="middle"
                    wrap
                    style={{ padding: token.paddingSM, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: token.borderRadiusLG, background: token.colorFillQuaternary }}
                  >
                    <div style={{ flex: '2 1 180px', minWidth: 0 }}>
                      <Typography.Text strong>{n.title}</Typography.Text>
                      <div>
                        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                          {t('idea_calc.niche.products_count', { count: formatCount(n.product_count, i18n.language) })}
                        </Typography.Text>
                      </div>
                    </div>
                    {stats(n).filter((s) => ['sv', 'conv', 'rr', 'price'].includes(s.key)).map((s) => (
                      <div key={s.key} style={{ flex: '1 1 90px' }}>
                        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}><HintLabel hint={t(`idea_calc.niche.tips.${s.key}`)}>{s.label}</HintLabel></Typography.Text>
                        <Typography.Text>{s.value}</Typography.Text>
                      </div>
                    ))}
                    <Flex gap="small" style={{ flex: 'none' }}>
                      <Button onClick={() => setView({ kind: 'detail', niche: n, preview: true })}>{t('idea_calc.niche.open_button')}</Button>
                      <Tooltip title={t('idea_calc.niche.tips.bind')}><Button type="primary" onClick={() => bind(n)}>{t('idea_calc.niche.bind_button')}</Button></Tooltip>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
              {mode === 'asin' && (
                <Button type="link" style={{ alignSelf: 'flex-start', padding: 0 }} onClick={() => setMode('keyword')}>
                  {t('idea_calc.niche.try_keyword')}
                </Button>
              )}
            </>
          )}
        </Flex>
      </Modal>

      <Modal
        open={view.kind === 'detail'}
        onCancel={() => setView(view.kind === 'detail' && view.preview ? { kind: 'search' } : { kind: 'closed' })}
        width={900}
        title={view.kind === 'detail' ? t('idea_calc.niche.detail_title', { title: view.niche.title }) : ''}
        footer={
          view.kind === 'detail' ? (
            view.preview ? (
              <Flex justify="end" gap="small">
                <Button onClick={() => setView({ kind: 'search' })}>{t('idea_calc.niche.back_to_list')}</Button>
                <Button type="primary" onClick={() => bind(view.niche)}>{t('idea_calc.niche.bind_this')}</Button>
              </Flex>
            ) : (
              <Flex justify="end" gap="small">
                <Button
                  danger
                  onClick={() => {
                    onUnbind();
                    setView({ kind: 'closed' });
                  }}
                >
                  {t('idea_calc.niche.unbind')}
                </Button>
                <Button onClick={openSearch}>{t('idea_calc.niche.change')}</Button>
              </Flex>
            )
          ) : null
        }
      >
        {view.kind === 'detail' && (
          <Flex vertical gap="middle">
            {!view.preview && view.niche.bound_at && (
              <Typography.Text type="secondary">
                {t('idea_calc.niche.bound_at', { date: new Date(view.niche.bound_at).toLocaleDateString(i18n.language) })}
              </Typography.Text>
            )}
            <Descriptions size="small" bordered column={{ xs: 1, sm: 2, md: 3 }} styles={{ content: { whiteSpace: 'nowrap' } }}>
              {stats(view.niche).map((s) => (
                <Descriptions.Item key={s.key} label={<HintLabel hint={t(`idea_calc.niche.tips.${s.key}`)}>{s.label}</HintLabel>}>{s.value}</Descriptions.Item>
              ))}
            </Descriptions>
            <Typography.Text strong>{t('idea_calc.niche.top_products')}</Typography.Text>
            <Table
              size="small"
              rowKey="asin"
              pagination={false}
              dataSource={view.niche.top_products}
              columns={[
                { key: 'asin', dataIndex: 'asin', title: 'ASIN' },
                { key: 'brand', dataIndex: 'brand', title: t('idea_calc.niche.col_brand'), render: (v: string | null) => v ?? '—' },
                { key: 'price', dataIndex: 'price', title: t('idea_calc.niche.col_price'), align: 'right', render: (v: number | null) => formatPrice(v) },
                { key: 'rating', dataIndex: 'rating', title: t('idea_calc.niche.col_rating'), align: 'right', render: (v: number | null) => (v == null ? '—' : v.toFixed(1)) },
                { key: 'reviews', dataIndex: 'reviews', title: t('idea_calc.niche.col_reviews'), align: 'right', render: (v: number | null) => formatCount(v, i18n.language) },
                { key: 'share', dataIndex: 'click_share', title: <HintLabel hint={t('idea_calc.niche.tips.click_share')}>{t('idea_calc.niche.col_click_share')}</HintLabel>, align: 'right', render: (v: number | null) => formatShare(v) },
              ]}
            />
          </Flex>
        )}
      </Modal>
    </div>
  );
};

export default NicheBinding;
